import { formatMoney, type Currency, type MoneyLocale } from "@talentkontor/domain";
import { currentLang, dayParts, formatDay, monthNames, setDezimalPunkt, uiLocale } from "./sprache";

/** Anzeigeformate wie im Design-Paket: 4.760,00 € · 24.09.2026 · September 2026. */

/**
 * Zahlenformat der angemeldeten Agentur (org_settings.locale): de-DE, de-AT, de-CH („CHF 1'234.50“) oder en-GB.
 * Die Rahmen der Portale setzen es beim Laden; bei englischer Oberfläche gilt en-GB (lib/sprache.ts).
 * Datum und Monatsnamen folgen der Oberflächensprache: 24.09.2026 · September 2026 bzw. 24/09/2026 · September 2026.
 */
let displayLocale: MoneyLocale = "de-DE";
const LOCALES: readonly MoneyLocale[] = ["de-DE", "de-AT", "de-CH", "en-GB"];
export function setDisplayLocale(locale: string | null | undefined): void {
  displayLocale = LOCALES.includes(locale as MoneyLocale) ? (locale as MoneyLocale) : "de-DE";
  // CP-029: Prozent- und Zahlentexte (numOf, pctOf) mit demselben Dezimalzeichen wie die Beträge
  setDezimalPunkt(displayLocale === "de-CH");
}
/**
 * Zahlenformat der Agentur unabhängig von der Oberflächensprache: für Texte, die in Belege gelangen (Rechnungszeilen der
 * Vorbelegung), damit eine englische Oberfläche die Dokumente nicht verändert.
 */
let orgLocaleValue: MoneyLocale = "de-DE";
export function setOrgLocale(locale: string | null | undefined): void {
  orgLocaleValue = LOCALES.includes(locale as MoneyLocale) ? (locale as MoneyLocale) : "de-DE";
}
export function orgLocale(): MoneyLocale {
  return orgLocaleValue;
}
/**
 * Zeitzone der angemeldeten Agentur (org_settings.timezone) für Kalendertage und Zeitpunkte ohne ausdrückliche Zone.
 * Fristen und „heute“ gelten in der Zone der Agentur; Chat, Glocke und Kalender rechnen zusätzlich in der Zone der Person.
 */
let displayTimeZone = "Europe/Berlin";
export function setDisplayTimeZone(zone: string | null | undefined): void {
  if (!zone) return;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    displayTimeZone = zone;
  } catch {
    // unbekannte Zone: bisherige bleibt
  }
}
export function displayZone(): string {
  return displayTimeZone;
}
export function numberLocale(): MoneyLocale {
  return displayLocale;
}
/** Dezimalzeichen für CSV-Exporte (Tabellenprogramme lesen die Zahl sonst als Text). */
export function decimalSeparator(): string {
  return displayLocale === "de-CH" || displayLocale === "en-GB" ? "." : ",";
}

export function money(cents: number | null | undefined, currency: string = "EUR", locale: MoneyLocale = displayLocale): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "–";
  return formatMoney(Math.round(cents), currency as Currency, locale);
}

/**
 * Beschriftung einer Währung in Auswahllisten: Code und Symbol („EUR €“); liefert die Sprache als Symbol nur den Code
 * (de-CH: „CHF“, auch „EUR“), steht er nur einmal da (SICHT-AGENTUR-GELD-14).
 */
export function currencyOptionLabel(code: string, locale: string = displayLocale): string {
  let sym = code;
  try {
    sym = new Intl.NumberFormat(locale, { style: "currency", currency: code }).formatToParts(0).find((p) => p.type === "currency")?.value ?? code;
  } catch {
    /* unbekannte Währung: nur der Code */
  }
  return sym && sym !== code ? `${code} ${sym}` : code;
}

/**
 * Intl-Ausgabe wie formatMoney (@talentkontor/domain) angleichen: schmales geschütztes Leerzeichen → geschütztes
 * Leerzeichen, de-CH-Tausendertrenner immer ' (neuere ICU liefert ’). So sehen ganze Beträge, Kurzbeträge und
 * Prozentwerte überall gleich aus wie die Beträge mit Cent (Portal einheitlich).
 */
export function normNumber(text: string, locale: string = displayLocale): string {
  const out = text.replace(/\u202F/g, "\u00A0");
  // de-CH: Intl liefert „CHF-1'500.00“; üblich ist „CHF -1'500.00“
  return locale === "de-CH" ? out.replace(/\u2019/g, "'").replace(/^([^\d\s-]+)-/, "$1\u00A0-") : out;
}

/** Zahl im Format der Agentur (de-DE 1.234,5 · en-GB 1,234.5 · de-CH 1'234.5). */
export function numberText(value: number, maxDigits = 0, locale: MoneyLocale = displayLocale): string {
  return normNumber(new Intl.NumberFormat(locale, { maximumFractionDigits: maxDigits }).format(value), locale);
}

/** Ganze Euro ohne Nachkommastellen (Creator-Kennzahlen im Design: „8.260 €“). */
export function moneyWhole(cents: number, currency: string = "EUR", locale: MoneyLocale = displayLocale): string {
  return normNumber(new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(Math.round(cents / 100)), locale);
}

/**
 * Rundgang R-21 (Design-Prüfung, Inhaber 02.11.2026): eine Regel für Beträge in Kacheln beider Portale. Bisher zeigten die Kacheln
 * der Agentur Cent („28.850,00 €“), die des Creator-Portals nie („1.750 €“, auch „27 €“ statt 26,60 €). Jetzt: ab 1.000 in der
 * Währung ganze Beträge (der genaue Betrag steht im Tooltip, KachelBetrag), darunter mit Cent. Listen, Tabellen und Belege
 * zeigen immer Cent (money).
 */
export function kachelBetrag(cents: number | null | undefined, currency: string = "EUR", locale: MoneyLocale = displayLocale): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return "–";
  // Prüf-Chat Nachtest (03.11.2026), Befund 14: null ohne Cent („CHF 0“ statt „CHF 0.00“, „0 €“ statt „0,00 €“)
  return Math.abs(cents) >= 100_000 || cents === 0 ? moneyWhole(cents, currency, locale) : money(cents, currency, locale);
}

/** Kurzbetrag für Achsen, Säulen und Balken („18,4k“ bzw. „18.4k“); unter 1.000 die ganze Zahl. */
export function shortAmount(cents: number): string {
  const e = cents / 100;
  return Math.abs(e) >= 1000 ? `${numberText(e / 1000, 1)}k` : String(Math.round(e));
}

export function percent(value: number, digits = 0, locale: string = displayLocale): string {
  return `${normNumber(new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value), locale)} %`;
}

/** 'YYYY-MM-DD' oder ISO-Zeitpunkt → 24.09.2026 bzw. 24/09/2026 (Zeitpunkte in der Zeitzone der Agentur). */
export function date(value: string | null | undefined, timeZone = displayTimeZone, locale: string = uiLocale()): string {
  if (!value) return "–";
  const lang = locale.startsWith("en") ? "en" : "de";
  // Eigenes Datumsformat der Person (PARITAET-R3-7), sonst nach der Sprache
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y = "", m = "", d = ""] = value.split("-");
    return formatDay(y, m, d, lang);
  }
  if (Number.isNaN(new Date(value).getTime())) return value;
  return formatDay(...dayParts(value, timeZone), lang);
}

export function time(value: string, timeZone = displayTimeZone, locale: string = uiLocale()): string {
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone }).format(new Date(value));
}

/** '2026-09' oder '2026-09-01' → „September 2026“ (Monatsname in der Oberflächensprache) */
export function monthLabel(key: string, withYear = true): string {
  const [y, m] = key.split("-");
  const name = monthNames(currentLang())[Number(m) - 1] ?? key;
  return withYear ? `${name} ${y}` : name;
}
export function monthShort(key: string): string {
  return monthNames(currentLang(), true)[Number(key.split("-")[1]) - 1] ?? key;
}
export function monthName(key: string): string {
  return monthNames(currentLang())[Number(key.split("-")[1]) - 1] ?? key;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

/** Heutiger Kalendertag in einer Zeitzone als 'YYYY-MM-DD'. */
export function today(timeZone = displayTimeZone): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
